import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { EventType, InteractionStatus } from '@azure/msal-browser';
import { getUserRolesSync, clearTokenCache, clearCurrentUser, setCurrentUser } from '../services/userRolesService';
import { obtenerPermisosPorRol } from '../api/transversal/permisosService';
import PermisoDto from '../dtos/permisoDto';

// Tipos para el contexto
interface UserInfo {
  name: string;
  email: string;
  username: string;
  oid: string;
  tenantId: string;
}

interface UserRolesContextType {
  // Estado de los roles
  userRoles: string[];
  userInfo: UserInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  userPermissions: PermisoDto[];
  
  // Funciones de verificación de roles
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasAllRoles: (roles: string[]) => boolean;
  
  // Funciones de verificación de permisos
  hasPermission: (codigoPermiso: string) => boolean;
  hasAnyPermission: (codigosPermiso: string[]) => boolean;
  hasAllPermissions: (codigosPermiso: string[]) => boolean;
  
  // Funciones de actualización
  refreshRoles: () => void;
  clearRoles: () => void;
  forceLoadUserData: () => void; // Función para forzar carga manual
}

// Crear el contexto
const UserRolesContext = createContext<UserRolesContextType | undefined>(undefined);

// Props del provider
interface UserRolesProviderProps {
  children: ReactNode;
}

// Provider del contexto
export const UserRolesProvider: React.FC<UserRolesProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userPermissions, setUserPermissions] = useState<PermisoDto[]>([]);

  // Función para obtener información del usuario desde el token
  const getUserInfoFromToken = () => {
    try {
      const activeAccount = instance.getActiveAccount();
      
      if (!activeAccount || !activeAccount.idTokenClaims) {
        return null;
      }

      const claims = activeAccount.idTokenClaims as any;
      
      return {
        name: claims.name || claims.preferred_username || 'Usuario',
        email: claims.email || claims.upn || '',
        username: claims.preferred_username || claims.upn || claims.name || '',
        oid: claims.oid || '',
        tenantId: claims.tid || ''
      };
    } catch (error) {
      console.error('Error al obtener información del usuario:', error);
      return null;
    }
  };

  // Función para cargar roles e información del usuario
  const loadUserData = async (forceRefresh: boolean = false) => {
    try {
      setIsLoading(true);
      
      let activeAccount = instance.getActiveAccount();

      if (!activeAccount && accounts.length > 0) {
        instance.setActiveAccount(accounts[0]);
        activeAccount = accounts[0];
      }
      
      if (activeAccount) {
        
        // Obtener roles del usuario
        console.log('🔐 Obteniendo roles del usuario...');
        const roles = await getUserRolesSync(instance, forceRefresh);
        console.log('📋 Roles obtenidos:', roles);
        setUserRoles(roles);
        
        // Obtener permisos basados en los roles
        console.log('🔑 Obteniendo permisos del usuario...');
        let permisos: PermisoDto[] = [];
        try {
          permisos = await obtenerPermisosPorRol(roles);
          console.log('🔑 Permisos obtenidos:', permisos);
          setUserPermissions(Array.isArray(permisos) ? permisos : []);
        } catch (permisosError) {
          console.error('⚠️ Error al obtener permisos:', permisosError);
          setUserPermissions([]);
        }
        
        // Obtener información del usuario
        console.log('👤 Obteniendo información del usuario...');
        const info = getUserInfoFromToken();
        console.log('📝 Información del usuario:', info);
        setUserInfo(info);
        setCurrentUser(info);
        
        setIsAuthenticated(true);
        
        console.log('✅ Datos del usuario cargados exitosamente:', {
          roles,
          permisos,
          userInfo: info,
          isAuthenticated: true
        });
      } else {
        // No hay usuario autenticado
        console.log('❌ No hay usuario autenticado, limpiando estado...');
        setUserRoles([]);
        setUserPermissions([]);
        setUserInfo(null);
        setIsAuthenticated(false);
        clearCurrentUser();
      }
    } catch (error) {
      console.error('💥 Error al cargar datos del usuario:', error);
      setUserRoles([]);
      setUserPermissions([]);
      setUserInfo(null);
      setIsAuthenticated(false);
      clearCurrentUser();
    } finally {
      setIsLoading(false);
      console.log('🏁 loadUserData finalizado');
    }
  };

  // Función para refrescar roles
  const refreshRoles = () => {
    // Forzar actualización del cache de tokens
    loadUserData(true);
  };

  // Función para forzar carga manual de datos del usuario
  const forceLoadUserData = () => {
    loadUserData(true);
  };

  // Función para limpiar roles (útil para logout)
  const clearRoles = () => {
    setUserRoles([]);
    setUserPermissions([]);
    setUserInfo(null);
    setIsAuthenticated(false);
    setIsLoading(false);
    clearCurrentUser();
    clearTokenCache(); // Limpiar también el cache de tokens
    
    // Limpiar también el timestamp de última actualización
    (window as any).lastRolesUpdate = 0;
    
    console.log('🧹 Estado de roles y permisos limpiado completamente');
  };

  // Funciones de verificación de roles
  const hasRole = (role: string): boolean => {
    return userRoles.includes(role);
  };

  const hasAnyRole = (roles: string[]): boolean => {
    return roles.some(role => userRoles.includes(role));
  };

  const hasAllRoles = (roles: string[]): boolean => {
    return roles.every(role => userRoles.includes(role));
  };

  // Funciones de verificación de permisos
  const hasPermission = (codigoPermiso: string): boolean => {
    return userPermissions?.some(permiso => permiso.codigoPermiso === codigoPermiso) ?? false;
  };

  const hasAnyPermission = (codigosPermiso: string[]): boolean => {
    return codigosPermiso.some(codigo => userPermissions?.some(permiso => permiso.codigoPermiso === codigo) ?? false);
  };

  const hasAllPermissions = (codigosPermiso: string[]): boolean => {
    return codigosPermiso.every(codigo => userPermissions?.some(permiso => permiso.codigoPermiso === codigo) ?? false);
  };

  // Efecto para cargar datos cuando MSAL termina de hidratar la sesión
  useEffect(() => {
    if (inProgress !== InteractionStatus.None) {
      return;
    }

    loadUserData(true);
    
    return () => {
    };
  }, [instance, accounts, inProgress]);

  // Efecto adicional para detectar cuando el usuario se desautentica
  useEffect(() => {
    let activeAccount = instance.getActiveAccount();
    if (!activeAccount && accounts.length > 0) {
      instance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }
    
    if (!activeAccount && isAuthenticated) {
      console.log('🔐 Usuario desautenticado detectado, limpiando roles...');
      clearRoles();
    }
  }, [instance, accounts, isAuthenticated]); // Escuchar cambios de cuenta

  // Efecto para escuchar eventos específicos de MSAL
  useEffect(() => {
    const handleLoginSuccess = () => {
      console.log('🎉 Evento de login exitoso recibido, cargando roles...');
      // Pequeño delay para asegurar que MSAL haya actualizado su estado
      setTimeout(() => {
        loadUserData();
      }, 100);
    };

    const handleLogout = () => {
      console.log('🚪 Evento de logout recibido, limpiando roles...');
      clearRoles();
    };

    // Agregar event listeners para eventos específicos de MSAL
    if (instance && instance.addEventCallback) {
      const callbackId = instance.addEventCallback((event) => {
        console.log('📡 Evento MSAL recibido:', event.eventType);
        
        if (event.eventType === EventType.LOGIN_SUCCESS) {
          handleLoginSuccess();
        } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
          handleLogout();
        }
      });

      // Cleanup
      return () => {
        if (callbackId && instance.removeEventCallback) {
          instance.removeEventCallback(callbackId);
        }
      };
    }
  }, [instance]);

  // Efecto para manejar renovación de tokens de manera más controlada
  useEffect(() => {
    const handleTokenRenewed = () => {
      console.log('🔄 Evento de renovación de token recibido, actualizando roles...');
      refreshRoles();
    };

    // Escuchar eventos personalizados de renovación de token
    window.addEventListener('tokenRenewed', handleTokenRenewed);

    // Intervalo más largo para evitar llamadas excesivas
    const interval = setInterval(async () => {
      if (isAuthenticated) {
        // Solo verificar si han pasado al menos 2 minutos desde la última actualización
        const now = Date.now();
        const lastUpdate = (window as any).lastRolesUpdate || 0;
        
        if (now - lastUpdate > 120000) { // 2 minutos
          try {
            const currentRoles = await getUserRolesSync(instance, false); // No forzar refresh en el intervalo
            if (JSON.stringify(currentRoles) !== JSON.stringify(userRoles)) {
              console.log('🔄 Roles actualizados, refrescando...');
              (window as any).lastRolesUpdate = now;
              refreshRoles();
            }
          } catch (error) {
            console.warn('Error al verificar roles en intervalo:', error);
          }
        }
      }
    }, 60000); // Verificar cada 1 minuto

    return () => {
      window.removeEventListener('tokenRenewed', handleTokenRenewed);
      clearInterval(interval);
    };
  }, [isAuthenticated, userRoles, instance]);

  const contextValue: UserRolesContextType = {
    userRoles,
    userInfo,
    isAuthenticated,
    isLoading,
    userPermissions,
    hasRole,
    hasAnyRole,
    hasAllRoles,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refreshRoles,
    clearRoles,
    forceLoadUserData
  };

  return (
    <UserRolesContext.Provider value={contextValue}>
      {children}
    </UserRolesContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useUserRolesContext = (): UserRolesContextType => {
  const context = useContext(UserRolesContext);
  
  if (context === undefined) {
    throw new Error('useUserRolesContext debe ser usado dentro de un UserRolesProvider');
  }
  
  return context;
};

// Hook simplificado para verificación de roles (sin contexto completo)
export const useUserRoles = () => {
  const { 
    hasRole, 
    hasAnyRole, 
    hasAllRoles, 
    userRoles, 
    isAuthenticated, 
    isLoading
  } = useUserRolesContext();
  
  return {
    hasRole,
    hasAnyRole,
    hasAllRoles,
    userRoles,
    isAuthenticated,
    isLoading
  };
};

// Hook simplificado para verificación de permisos
export const useUserPermissions = () => {
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    userPermissions,
    isAuthenticated,
    isLoading
  } = useUserRolesContext();
  
  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    userPermissions,
    isAuthenticated,
    isLoading
  };
};

// Hook para debugging - muestra el estado completo del contexto
export const useUserRolesDebug = () => {
  const context = useUserRolesContext();
  
  // Log del estado cada vez que cambie
  useEffect(() => {
    console.log('🔍 DEBUG - Estado del contexto de roles:', {
      userRoles: context.userRoles,
      userPermissions: context.userPermissions,
      userInfo: context.userInfo,
      isAuthenticated: context.isAuthenticated,
      isLoading: context.isLoading
    });
  }, [context.userRoles, context.userPermissions, context.userInfo, context.isAuthenticated, context.isLoading]);
  
  return context;
};
